import { getLedgerListings } from 'graphql/ledger/queries';
import { useGetOrderParties } from 'hooks/useGetOrderParties';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
import { useParams } from 'react-router-dom';
import { listingItemtype, PartyAccounting } from 'types/ledger';
import useAlert from 'utils/alert';
import { AccountingInvolvedParty } from 'v2-types/order';

import { addpartyLedgerAssociation, updateLedgerAssociation } from './services/mutations';
import { AddAndUpdatePartyLedgerForm } from './types';
import { isArrayOfStrings } from './utils';
import { getInitialValues } from './utils/initialValues';

// Empty percent inputs are stored as '' and count as 0 instead of NaN.
const toFraction = (value: number | string | null | undefined) => (parseFloat(String(value ?? '')) || 0) / 100;

type UseAddAndUpdatePartyLedgerEntryParams = {
  accountingEntryId: string;
  prevAccountingPartyEntry?: AccountingInvolvedParty;
  handleClose: () => void;
  handleSuccess?: () => void;
  kindAllowedAssociationList?: string | string[];
  partyKindFilter?: string[];
  percent?: number;
};

export const useAddAndUpdatePartyLedgerEntry = ({
  accountingEntryId,
  prevAccountingPartyEntry,
  handleClose,
  handleSuccess,
  kindAllowedAssociationList,
  partyKindFilter,
  percent,
}: UseAddAndUpdatePartyLedgerEntryParams) => {
  const { t } = useTranslation();
  const { id: orderId } = useParams<{ id: string }>();
  const [allowedAssociations, setAllowedAssociations] = useState<listingItemtype[]>([]);
  const [currentPartySelected, setCurrentPartySelected] = useState<PartyAccounting | null>(null);
  const [createErrors, setCreateErrors] = useState<string[]>([]);
  const showAlert = useAlert();
  const methods = useForm<AddAndUpdatePartyLedgerForm>({
    defaultValues: getInitialValues(prevAccountingPartyEntry, percent),
  });

  const { data: getOrderPartiesData } = useGetOrderParties({
    orderId,
    kindsFilter: partyKindFilter,
    queryConfig: { refetchOnWindowFocus: false },
  });

  const parties = (getOrderPartiesData?.getOrder.parties || []) as PartyAccounting[];

  const getLedgerAllowedAssociations = useQuery(
    ['allowed-ledger-Associations', 'allowed_ledger_associations'],
    () => getLedgerListings('allowed_ledger_associations'),
    {
      refetchOnWindowFocus: false,
      onSuccess: (data) => {
        if (data) {
          const unfilterAssociationList = data.getListing.entries || [];
          let allowedAssociationsKind = unfilterAssociationList;

          if (kindAllowedAssociationList) {
            if (isArrayOfStrings(kindAllowedAssociationList)) {
              allowedAssociationsKind = unfilterAssociationList.filter((association) =>
                kindAllowedAssociationList.some((k) =>
                  association.code.toLowerCase().includes(k.toLowerCase())
                )
              );
            } else {
              allowedAssociationsKind = unfilterAssociationList.filter((association) =>
                association.code.toLowerCase().includes(kindAllowedAssociationList.toLowerCase())
              );
            }
          }

          if (!prevAccountingPartyEntry) {
            methods.setValue('kind', allowedAssociationsKind[0].code);
          }
          setAllowedAssociations(allowedAssociationsKind);
        }
      },
    }
  );

  const { mutate: createLedgerAssociation, ...createLedgerAssociationResponse } = useMutation(
    addpartyLedgerAssociation,
    {
      onSuccess: (data) => {
        if (data) {
          showAlert(t('documents:success-updating'), 'success');
          createLedgerAssociationResponse.reset();
          methods.reset();
          setCreateErrors([]);
          handleClose();
        }
      },
      onError: (e: any) => {
        const { errors = ['Error Adding Association'] } = e?.response || {};
        const errorsMapped = errors.map((error: any) => (typeof error === 'string' ? error : error.message));
        setCreateErrors([...errorsMapped]);
      },
    }
  );

  const { mutate: editLedgerAssociation, ...editLedgerAssociationResponse } = useMutation(
    updateLedgerAssociation,
    {
      onSuccess: (data) => {
        if (data) {
          if (handleSuccess) handleSuccess();
          editLedgerAssociationResponse.reset();
          methods.reset();
          showAlert(t('documents:success-updating'), 'success');
          setCreateErrors([]);
          handleClose();
        }
      },
      onError: (e: any) => {
        const { errors = ['Error Adding Association'] } = e?.response || {};
        const errorsMapped = errors.map((error: any) => (typeof error === 'string' ? error : error.message));
        setCreateErrors([...errorsMapped]);
      },
    }
  );

  const handleAssociationCreateLedger = methods.handleSubmit((data) => {
    if (prevAccountingPartyEntry) {
      editLedgerAssociation({
        id: orderId,
        accountingId: accountingEntryId,
        data: {
          at_closing_percent: toFraction(data.at_closing_percent),
          payment_id: data.payment_id,
          percent: toFraction(data.percent),
        },
        kind: data.kind,
        partyId: prevAccountingPartyEntry._id,
      });
      return;
    }
    createLedgerAssociation({
      id: orderId,
      accountingId: accountingEntryId,
      data: {
        at_closing_percent: toFraction(data.at_closing_percent),
        payment_id: data.payment_id,
        percent: toFraction(data.percent),
      },
      kind: data.kind,
      partyId: data.party_id,
    });
  });

  return {
    t,
    methods,
    handleAssociationCreateLedger,
    allowedAssociations,
    loadingAssociations: getLedgerAllowedAssociations.isLoading,
    setCurrentPartySelected,
    currentPartySelected,
    createErrors,
    loadingEditAssociation: editLedgerAssociationResponse.isLoading,
    loadingAddAssociation: createLedgerAssociationResponse.isLoading,
    parties,
  };
};
